/**
 * Bulk BOQ Mapping — admin-only page to select many unmapped POs and assign a BOQ item at once.
 * Reachable at /pos/bulk-map. Only POs missing a boq_item_id are shown.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MagnifyingGlass, ArrowLeft, LinkSimple, CheckCircle, WarningOctagon } from "@phosphor-icons/react";
import { toast } from "sonner";
import BOQItemPicker from "@/components/BOQItemPicker";

const PAGE_SIZE = 50;
const fmtINR = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function POBulkMap() {
  const navigate = useNavigate();
  const { activeProjectId, activeProject } = useProject();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [pos, setPos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounceSearch, setDebounceSearch] = useState("");
  const [poType, setPoType] = useState("material"); // 'material' | 'contract' | 'all'
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [boqItemId, setBoqItemId] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounceSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async (targetPage = page) => {
    if (!activeProjectId) return;
    setLoading(true);
    try {
      const params = { project_id: activeProjectId, boq_missing: true, page: targetPage, page_size: PAGE_SIZE };
      if (poType !== "all") params.po_type = poType;
      if (debounceSearch) params.search = debounceSearch;
      const { data } = await api.get("/pos", { params });
      setPos(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { setPage(1); load(1); /* eslint-disable-next-line */ }, [activeProjectId, poType, debounceSearch]);
  useEffect(() => { load(page); /* eslint-disable-next-line */ }, [page]);

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllOnPage = () => setSelected((prev) => {
    const pageIds = pos.map((p) => p.id);
    const allSelected = pageIds.every((id) => prev.has(id));
    const next = new Set(prev);
    if (allSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const allOnPageSelected = pos.length > 0 && pos.every((p) => selected.has(p.id));

  const selectedTotal = useMemo(() =>
    pos.filter((p) => selected.has(p.id)).reduce((s, p) => s + (p.total_amount || 0), 0),
    [pos, selected]
  );

  const doApply = async () => {
    if (!boqItemId || selected.size === 0) return;
    setApplying(true);
    try {
      const { data } = await api.post("/pos/bulk-map", {
        po_ids: Array.from(selected),
        boq_item_id: boqItemId,
      });
      setResult(data);
      if (data.updated_count > 0) toast.success(`${data.updated_count} PO(s) mapped to ${data.boq_item_name}`);
      if (data.skipped?.length) toast.error(`${data.skipped.length} PO(s) skipped — see details`);
      clearSelection();
      await load(page);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setApplying(false); }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-neutral-500 text-sm">
        Admin access required to bulk-map POs.
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto p-6 lg:p-8" data-testid="po-bulk-map-page">
      <button onClick={() => navigate(-1)} className="text-xs text-neutral-500 hover:text-primary flex items-center gap-1 mb-3">
        <ArrowLeft size={12} /> Back
      </button>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <LinkSimple size={30} className="text-primary" weight="fill" /> Bulk BOQ Mapping
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl leading-relaxed">
            Attach many un-mapped POs to a single BOQ line item in one action.
            Amounts, statuses and approval history stay untouched; linked invoices are re-pointed automatically.
            {activeProject?.name && <> Project: <span className="text-neutral-800 font-medium">{activeProject.name}</span></>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="bg-white border border-neutral-200 rounded-sm px-4 py-2">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Selected</div>
            <div className="font-display text-2xl font-bold tabular-nums text-primary" data-testid="bulk-map-selected-count">{selected.size}</div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-sm px-4 py-2">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Selected Value</div>
            <div className="font-display text-2xl font-bold tabular-nums text-neutral-900">₹{fmtINR(selectedTotal)}</div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-sm px-4 py-2">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Total Unmapped</div>
            <div className="font-display text-2xl font-bold tabular-nums text-neutral-900" data-testid="bulk-map-total-unmapped">{total}</div>
          </div>
        </div>
      </div>

      {/* BOQ target picker */}
      <div className="bg-primary/5 border border-primary/30 rounded-sm p-4 mb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[300px]">
            <BOQItemPicker
              projectId={activeProjectId}
              value={boqItemId}
              onChange={(id) => setBoqItemId(id)}
              label="Target BOQ Item — the selected POs will all be mapped to this item"
              testId="bulk-map-target-boq"
            />
          </div>
          <Button
            className="rounded-sm bg-primary hover:bg-[#0039CC] h-11 gap-2"
            disabled={!boqItemId || selected.size === 0}
            onClick={() => setApplyOpen(true)}
            data-testid="bulk-map-apply-btn"
          >
            <LinkSimple size={16} /> Apply to {selected.size} PO{selected.size !== 1 ? "s" : ""}
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search PO # / vendor / category…"
            className="pl-8 rounded-sm h-10 w-[300px]"
            data-testid="bulk-map-search"
          />
        </div>
        <div className="flex gap-1 border border-neutral-200 rounded-sm p-0.5">
          {["material", "contract", "all"].map((t) => (
            <button
              key={t}
              onClick={() => setPoType(t)}
              data-testid={`bulk-map-type-${t}`}
              className={
                "text-xs px-3 py-1.5 rounded-sm capitalize " +
                (poType === t ? "bg-primary text-white" : "text-neutral-600 hover:text-neutral-900")
              }
            >
              {t === "all" ? "All Types" : t + " POs"}
            </button>
          ))}
        </div>
        {selected.size > 0 && (
          <button onClick={clearSelection} className="text-xs text-neutral-500 hover:text-primary underline ml-auto" data-testid="bulk-map-clear-selection">
            Clear selection ({selected.size})
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-neutral-200 bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
          <div className="col-span-1 flex items-center">
            <input type="checkbox" checked={allOnPageSelected} onChange={toggleAllOnPage} data-testid="bulk-map-select-all-page" className="h-4 w-4 accent-primary" />
          </div>
          <div className="col-span-2">PO #</div>
          <div className="col-span-3">Vendor</div>
          <div className="col-span-2">Legacy Category</div>
          <div className="col-span-1">Type</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-1 text-right">Date</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-neutral-400 text-sm">Loading…</div>
        ) : pos.length === 0 ? (
          <div className="p-16 text-center">
            <CheckCircle size={40} className="mx-auto text-[#137333] mb-3" weight="fill" />
            <div className="font-display text-lg font-bold mb-1">All caught up</div>
            <div className="text-sm text-neutral-500 mb-4">No unmapped POs match this filter.</div>
            <Link to="/pos"><Button variant="outline" className="rounded-sm">Back to Material POs</Button></Link>
          </div>
        ) : (
          pos.map((p) => {
            const isSel = selected.has(p.id);
            return (
              <div
                key={p.id}
                onClick={() => toggleOne(p.id)}
                className={
                  "grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-neutral-100 cursor-pointer text-sm " +
                  (isSel ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-neutral-50/70")
                }
                data-testid={`bulk-map-row-${p.id}`}
              >
                <div className="col-span-1 flex items-center">
                  <input type="checkbox" checked={isSel} onChange={() => toggleOne(p.id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 accent-primary" data-testid={`bulk-map-checkbox-${p.id}`} />
                </div>
                <div className="col-span-2 self-center font-medium tabular-nums text-primary truncate">{p.po_number}</div>
                <div className="col-span-3 self-center text-neutral-800 truncate">{p.vendor_name || "—"}</div>
                <div className="col-span-2 self-center text-neutral-500 truncate">{p.category || <span className="text-neutral-300 italic">none</span>}</div>
                <div className="col-span-1 self-center text-[11px] text-neutral-500 capitalize">{p.po_type}</div>
                <div className="col-span-2 self-center text-right tabular-nums text-neutral-900">₹{fmtINR(p.total_amount)}</div>
                <div className="col-span-1 self-center text-right text-[11px] text-neutral-500">{(p.created_at || "").slice(0, 10)}</div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-neutral-500">
          <div>Page {page} of {totalPages} · {total} unmapped POs</div>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="bulk-map-prev">Previous</Button>
            <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="bulk-map-next">Next</Button>
          </div>
        </div>
      )}

      {/* Apply confirmation dialog */}
      <Dialog open={applyOpen} onOpenChange={(o) => !applying && setApplyOpen(o)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="bulk-map-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Confirm bulk mapping</DialogTitle>
            <DialogDescription>
              You are about to attach <span className="font-medium text-neutral-900">{selected.size}</span> PO{selected.size !== 1 ? "s" : ""} to the selected BOQ item.
              Line items, totals, statuses and approval history remain unchanged.
              Any linked invoices will be re-pointed automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-neutral-500 border border-neutral-200 rounded-sm p-3 bg-neutral-50">
            This action is audited (<code className="text-[10px]">po.bulk_mapping_updated</code>). POs from a different project than the BOQ item will be skipped with a reason.
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-sm" onClick={() => setApplyOpen(false)} disabled={applying}>Cancel</Button>
            <Button
              className="rounded-sm bg-primary hover:bg-[#0039CC]"
              onClick={async () => { await doApply(); setApplyOpen(false); }}
              disabled={applying}
              data-testid="bulk-map-confirm-btn"
            >
              {applying ? "Applying…" : `Apply to ${selected.size} PO${selected.size !== 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result dialog */}
      {result && (
        <Dialog open onOpenChange={() => setResult(null)}>
          <DialogContent className="rounded-sm max-w-lg" data-testid="bulk-map-result-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <CheckCircle size={20} weight="fill" className="text-[#137333]" /> Mapping complete
              </DialogTitle>
              <DialogDescription>
                <span className="text-neutral-900 font-medium">{result.updated_count}</span> PO(s) mapped to{" "}
                <span className="text-primary font-medium">{result.boq_item_name}</span>.{" "}
                {result.invoice_cascade_total > 0 && <>{result.invoice_cascade_total} invoice(s) re-linked.</>}
              </DialogDescription>
            </DialogHeader>
            {result.skipped?.length > 0 && (
              <div className="border border-[#FCE39E] bg-[#FEF7E0] rounded-sm p-3">
                <div className="text-xs font-medium text-[#B06000] mb-2 flex items-center gap-1.5">
                  <WarningOctagon size={12} weight="fill" /> Skipped ({result.skipped.length}):
                </div>
                <div className="text-[11px] space-y-1 max-h-[200px] overflow-y-auto" data-testid="bulk-map-skipped-list">
                  {result.skipped.map((s, i) => (
                    <div key={i}>
                      <span className="font-medium text-neutral-700">{s.po_number || s.po_id}:</span>{" "}
                      <span className="text-[#C5221F]">{s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button className="rounded-sm bg-primary hover:bg-[#0039CC]" onClick={() => setResult(null)} data-testid="bulk-map-result-done">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
